import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReportV5Detailed from '../components/report_v5_detailed/ReportV5Detailed';
import ReportIcon from '../components/report_v5/ReportIcons';
import {
  fetchReportV5Detailed,
  regenerateReportV5,
  REPORT_STATUS,
} from '../api/reports';
import { toReportV5DetailedViewModel } from '../adapters/reportV5DetailedAdapter';
import { markPending, markSeen } from '../utils/pendingReports';

/**
 * V5 详细报告页（付费 6-Page）。
 *
 * 状态机：idle → loading → (generating 1.5s 轮询) → done / failed / error
 * 与粗略报告的状态机一致，区别只在：
 *  - 调 fetchReportV5Detailed（默认 ?variant=detailed）
 *  - 走 toReportV5DetailedViewModel 拼出含 detailed 子块的 VM
 *  - 渲染 ReportV5Detailed
 *
 * 路由参数 id：
 *  - 'preview' / 静态 /report/v5/preview/detailed → 走 mock
 *  - 其他 → 走真后端
 *
 * 后端 schema / endpoint 见 docs/archive/v5/AI-HANDOFF.md 2026-05-20 14:00 frontend 提议
 * （后端实现完成后会换签）。
 */
const POLL_INTERVAL_MS = 1500;
const POLL_TIMEOUT_MS = 60000;

export default function ReportV5DetailedPage() {
  const params = useParams();
  const id = params.id || 'preview';
  const navigate = useNavigate();

  const [status, setStatus] = useState(REPORT_STATUS.IDLE);
  const [vm, setVm] = useState(null);
  const [error, setError] = useState(null);
  const [canRetry, setCanRetry] = useState(false);
  const [phase, setPhase] = useState(null);  // ?variant=detailed 轮询的 phase 值
  const pollStartedAt = useRef(null);
  const timerRef = useRef(null);

  const applyResult = useCallback(({ status: s, payload, error: err, canRetry: cr, phase: ph }) => {
    if (s === REPORT_STATUS.GENERATING) {
      pollStartedAt.current = pollStartedAt.current || Date.now();
      setStatus(REPORT_STATUS.GENERATING);
      setPhase(ph || null);
      return;
    }
    if (s === REPORT_STATUS.FAILED || s === REPORT_STATUS.ERROR) {
      pollStartedAt.current = null;
      setStatus(s);
      setError(err);
      setCanRetry(Boolean(cr));
      return;
    }
    pollStartedAt.current = null;
    setVm(toReportV5DetailedViewModel(payload));
    setStatus(REPORT_STATUS.DONE);
  }, []);

  const load = useCallback(async () => {
    setStatus(REPORT_STATUS.LOADING);
    setError(null);
    setCanRetry(false);
    pollStartedAt.current = null;
    const res = await fetchReportV5Detailed(id);
    applyResult(res);
  }, [id, applyResult]);

  const retry = useCallback(async () => {
    setStatus(REPORT_STATUS.LOADING);
    setError(null);
    setCanRetry(false);
    pollStartedAt.current = Date.now();
    const res = await regenerateReportV5(id);
    applyResult(res);
  }, [id, applyResult]);

  useEffect(() => {
    load();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [load]);

  // 详细报告等价处理：GENERATING -> markPending('detailed')；
  // DONE -> markSeen（用户已在等待页看到）。
  // 弹气泡由 PageLayout 后台 polling 检测 done 后 markReady 触发。
  useEffect(() => {
    if (id === 'preview') return;
    if (status === REPORT_STATUS.GENERATING) markPending('detailed', id);
    else if (status === REPORT_STATUS.DONE) markSeen('detailed', id);
  }, [status, id]);

  useEffect(() => {
    if (status !== REPORT_STATUS.GENERATING) return undefined;
    const tick = async () => {
      const elapsed = Date.now() - (pollStartedAt.current || Date.now());
      if (elapsed > POLL_TIMEOUT_MS) {
        setStatus(REPORT_STATUS.ERROR);
        setError('报告生成超时，请稍后刷新页面再试。');
        setCanRetry(true);
        return;
      }
      const res = await fetchReportV5Detailed(id);
      if (res.status === REPORT_STATUS.GENERATING) {
        timerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
        return;
      }
      applyResult(res);
    };
    timerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [status, id, applyResult]);

  if (status === REPORT_STATUS.LOADING || status === REPORT_STATUS.IDLE) {
    return (
      <StateView
        icon="spin"
        title="正在加载详细报告…"
        tip="解析答题数据中"
        secondaryLabel="返回首页"
        onSecondary={() => navigate('/')}
      />
    );
  }
  if (status === REPORT_STATUS.GENERATING) {
    // 后端 ?variant=detailed 在 polling 时给两种 phase：
    //   - rough_pending:    粗略还在生成（detailed 必须等粗略 done 才能开始）
    //   - detailed_pending: 粗略 done，detailed 在生成中
    const isRoughPending = phase === 'rough_pending';
    return (
      <StateView
        icon="spin"
        title={isRoughPending ? '粗略报告正在生成中…' : '详细报告正在生成中…'}
        tip={
          isRoughPending
            ? '基础版（3 页）报告需要先生成完毕，详细版（6 页）会随后自动开始撰写。'
            : 'AI 正在按 6 页的深度结构撰写专属解读，通常需要 1-3 分钟，请稍候。'
        }
        secondaryLabel="返回首页"
        onSecondary={() => navigate('/')}
      />
    );
  }
  if (status === REPORT_STATUS.FAILED) {
    return (
      <StateView
        error
        icon="alert-triangle"
        title="详细报告生成失败"
        tip={error || '请重试一次。如多次失败，请联系管理员。'}
        primaryLabel={canRetry ? '重新生成' : null}
        onPrimary={canRetry ? retry : null}
        secondaryLabel="刷新页面"
        onSecondary={load}
      />
    );
  }
  if (status === REPORT_STATUS.ERROR) {
    return (
      <StateView
        error
        icon="shield"
        title="详细报告无法加载"
        tip={error || '请稍后刷新重试，或返回粗略报告页查看免费内容。'}
        primaryLabel="重试"
        onPrimary={load}
      />
    );
  }
  // 详细报告返回粗略报告页（不是 /history）；preview 模式无返回入口
  const handleBack = id !== 'preview' ? () => navigate(`/report/v5/${id}`) : undefined;
  return <ReportV5Detailed vm={vm} assessmentId={id} onBack={handleBack} />;
}

function StateView({ icon, title, tip, error, primaryLabel, onPrimary, secondaryLabel, onSecondary }) {
  return (
    <div className="v5d-root">
      <div className="v5d-container">
        <div className={`v5d-state ${error ? 'error' : ''}`}>
          <div className="v5d-state-icon">
            {icon === 'spin' ? <span className="v5d-state-spin" /> : <ReportIcon name={icon} size={28} />}
          </div>
          <h2>{title}</h2>
          {tip && <p>{tip}</p>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 18 }}>
            {primaryLabel && onPrimary && (
              <button type="button" className="v5d-state-btn" onClick={onPrimary}>{primaryLabel}</button>
            )}
            {secondaryLabel && onSecondary && (
              <button
                type="button"
                className="v5d-state-btn"
                style={{ background: 'transparent', color: 'var(--v5-accent)', border: '1px solid var(--v5-line)' }}
                onClick={onSecondary}
              >
                {secondaryLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
